import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Typo } from '@/components/ui/typography';
import { useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { formatCurrency } from '@/lib/accounting';
import { formatDate } from '@/lib/date';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { Calendar, ShoppingCart, Trash2 } from 'lucide-react';
import { useState } from 'react';

// Remove the local CartItem interface - we'll use UserCartItem from the hook

interface StickyCartProps {
  className?: string;
}

export function StickyCart({ className = '' }: StickyCartProps) {
  const [isLoading, setIsLoading] = useState(false);

  const { cart: cartItems, total: cartTotal, count: totalItems, removeFromCart, isFetching } = useUserGlobalCart();

  const handleRemoveItem = async (rowId: string) => {
    setIsLoading(true);
    try {
      await removeFromCart({ rowId });
    } catch (error) {
      console.error('Error removing cart item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date helper
  const formatCartItemDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return formatDate(dateString);
    } catch {
      return dateString;
    }
  };

  if (cartItems.length === 0) {
    return (
      <Card className={cn('sticky top-36 h-fit', className)}>
        <CardContent className="p-6 text-center">
          <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <Typo variant="h5" className="mb-2 font-medium text-gray-900">
            Giỏ hàng trống
          </Typo>
          <Typo variant="muted">Thêm dịch vụ vào giỏ hàng để tiếp tục</Typo>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('sticky top-36 h-fit rounded-3xl', className)}>
      {/* <CardHeader className="pb-4">
                <CardTitle className="flex items-center justify-start gap-2">
                    <ShoppingCart className="h-5 w-5 text-gray-600" />
                    <Typo variant="h5" className="text-lg">
                        Giỏ hàng ({totalItems})
                    </Typo>
                </CardTitle>
            </CardHeader> */}

      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <Typo variant="h5" className="font-medium">
            Giỏ hàng ({totalItems})
          </Typo>
        </div>
        <Separator />
        {/* Cart Items */}
        <div className="max-h-64 space-y-3 overflow-y-auto">
          {cartItems.map((item) => (
            <div key={item.id} className="rounded-lg border p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <Typo variant="small" className="line-clamp-2 truncate font-medium whitespace-break-spaces text-gray-900">
                    {item.name}
                    {item.options?.type_name && ` - ${item.options.type_name}`} {item.qty && ` x ${item.qty}`}
                  </Typo>
                  <Typo variant="muted" className="mt-1 flex items-center text-xs">
                    <Calendar className="mr-1 h-3 w-3" />
                    {formatCartItemDate(item.options?.date)}
                  </Typo>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleRemoveItem(item.rowId)}
                  className="size-8 p-0 text-gray-400 hover:text-red-500"
                  disabled={isLoading || isFetching}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>

              <div className="flex items-center justify-between">
                {/* <div className="flex items-center border rounded">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleUpdateQuantity(item, item.qty - 1)}
                                        className="h-7 w-7 p-0"
                                        disabled={item.qty <= 1 || isLoading || isFetching}
                                    >
                                        <Minus className="h-3 w-3" />
                                    </Button>
                                    <Typo variant="small" className="px-2 font-medium w-8 text-center">
                                        {item.qty}
                                    </Typo>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleUpdateQuantity(item, item.qty + 1)}
                                        className="h-7 w-7 p-0"
                                        disabled={isLoading || isFetching}
                                    >
                                        <Plus className="h-3 w-3" />
                                    </Button>
                                </div> */}

                <div className="text-right">
                  <Typo variant="h6" className="text-primary font-medium">
                    {formatCurrency(item.subtotal)}
                  </Typo>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* Total */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-lg font-semibold">
            <Typo variant="h5">Tổng cộng:</Typo>
            <Typo variant="h5" className="text-primary">
              {formatCurrency(cartTotal)}
            </Typo>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-4">
          <Button asChild variant="outline" className="w-full rounded-full" size="lg">
            <Link href="/cart">
              <ShoppingCart className="h-4 w-4" />
              Xem giỏ hàng
            </Link>
          </Button>
          <Button asChild disabled className="w-full rounded-full disabled:opacity-50" size="lg">
            <Link href="/cart">{isLoading || isFetching ? 'Đang xử lý...' : 'Tiếp tục'}</Link>
          </Button>
        </div>

        {/* Additional info */}
        {/* <div className="mt-4 text-center">
                    <Typo variant="muted" className="text-xs">
                        🔒 Thanh toán an toàn và bảo mật
                    </Typo>
                    <Typo variant="muted" className="mt-1 text-xs">
                        Hỗ trợ hủy vé miễn phí
                    </Typo>
                </div> */}
      </CardContent>
    </Card>
  );
}
